import { classNames } from "primereact/utils";
import styles from "../PortalCalendar.module.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useAppContext } from "../../../../AppContext";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { ICalendarWrapperViewModel } from "../models/calendarWrapperViewModel";
import { ICalendarEventModel } from "../models/calendarEventModel";
import { routePaths } from "../../../../common/infrastructure/routes/routePaths";
import { getCurrentUserRole } from "../../../../common/infrastructure/helpers/userHelper";
import { UserRole } from "../../../../common/enums";
import { isReservationActive } from "../../../../common/infrastructure/utilities/reservationUtility";
import { getErrorToast } from "../../../../common/infrastructure/utilities/toastUtility";
import { usePreReservationFormForEvent } from "../../../../common/preReservationForms/usePreReservationFormForReservation";
import { IPreReservationFormSubmission } from "../../../../common/preReservationForms/models";
import { IPrepaymentDialogConfirmResult } from "../../../../common/components/PrepaymentDialog/PrepaymentDialog";

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

interface TileContentProps {
  date: Date;
  view: string;
}

export const usePortalCalendar = () => {
  const { selectedStudioId, isAuthenticated, currentUser } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mutate } = useApiMutation();
  const queryClient = useQueryClient();
  const { isDesktopOrTablet } = useAppContext();
  const urlEventId = searchParams.get("eventId");
  const [showEventId, setShowEventId] = useState<number>();

  const navigate = useNavigate();
  const [showUserSelectionDialog, setShowUserSelectionDialog] = useState(false);
  const [selectedEventName, setSelectedEventName] = useState<string | null>(
    null,
  );
  const [prepaymentDialogContext, setPrepaymentDialogContext] = useState<{
    eventId: number;
    userId: number;
  } | null>(null);

  // Pre-reservation consent form gate for the calendar sign-up flow.
  const [formDialogContext, setFormDialogContext] = useState<{
    eventId: number;
    userId: number;
  } | null>(null);
  const formSubmissionRef = useRef<{
    eventId: number;
    userId: number;
    submission: IPreReservationFormSubmission;
  } | null>(null);
  const preReservationForm = usePreReservationFormForEvent(
    formDialogContext?.eventId,
  );

  const { data, isLoading } = useApiQuery<ICalendarWrapperViewModel>({
    queryKey: [endpointsMetadata.portalCalendar.getAll],
    endpoint: `${endpointsMetadata.portalCalendar.getAll}?studioId=${selectedStudioId}`,
    enabled:
      selectedStudioId > 0 &&
      (!isAuthenticated || getCurrentUserRole(currentUser) === UserRole.User),
    globalIsLoading: false,
    disableErrorToast: false,
    retry: false,
  });

  const urlEventParsedId =
    urlEventId && !Number.isNaN(Number(urlEventId))
      ? Number(urlEventId)
      : undefined;

  // Whether the linked event is part of the (visible) calendar listing.
  const isUrlEventInCalendar = useMemo(() => {
    if (!urlEventParsedId || !data?.events) return false;
    return data.events.some((day) =>
      day.events.some((e) => e.id === urlEventParsedId),
    );
  }, [urlEventParsedId, data]);

  // A class can be hidden from the calendar listing (ShowInUserPortal = false) yet still be
  // reachable via a direct link (?eventId=...). When the linked event isn't part of the
  // visible calendar, fetch it on its own so the user can still view it and reserve a spot.
  const hiddenEventEnabled =
    !!urlEventParsedId &&
    !isLoading &&
    !!data &&
    !isUrlEventInCalendar &&
    selectedStudioId > 0;

  const { data: fetchedHiddenEvent } = useApiQuery<ICalendarEventModel>({
    queryKey: [endpointsMetadata.portalCalendar.getEvent, urlEventParsedId],
    endpoint: `${endpointsMetadata.portalCalendar.getEvent}?eventId=${urlEventParsedId}`,
    enabled: hiddenEventEnabled,
    globalIsLoading: false,
    disableErrorToast: true,
    retry: false,
  });

  // Persist the fetched hidden event in state. The query is keyed on the URL's
  // eventId, which gets stripped when a dialog closes — at that point the query
  // data would disappear and break later steps (e.g. the prepayment dialog needs
  // the event after the details dialog has closed). Caching it in state keeps it
  // available for the whole reservation flow.
  const [hiddenEvent, setHiddenEvent] = useState<ICalendarEventModel | undefined>(
    undefined,
  );
  useEffect(() => {
    if (fetchedHiddenEvent) {
      setHiddenEvent(fetchedHiddenEvent);
    }
  }, [fetchedHiddenEvent]);

  // Open the details dialog for a hidden (direct-link-only) event once it has
  // loaded — but only the first time, so closing the dialog (or moving on to the
  // prepayment dialog) doesn't bounce it back open.
  const autoOpenedDeepLinkRef = useRef(false);
  useEffect(() => {
    if (hiddenEvent?.id && !autoOpenedDeepLinkRef.current) {
      autoOpenedDeepLinkRef.current = true;
      setShowEventId(hiddenEvent.id);
    }
  }, [hiddenEvent]);

  const distinctEventNames = useMemo(() => {
    if (!data?.events) return [];
    const seen = new Map<string, string>();
    data.events.forEach((day) => {
      day.events.forEach((event) => {
        const key = event.name.trim().toLowerCase();
        if (!seen.has(key)) seen.set(key, event.name.trim());
      });
    });
    return Array.from(seen.values());
  }, [data]);

  useEffect(() => {
    if (selectedEventName && distinctEventNames.length > 0) {
      const stillExists = distinctEventNames.some(
        (n) => n.toLowerCase() === selectedEventName.toLowerCase(),
      );
      if (!stillExists) setSelectedEventName(null);
    }
  }, [distinctEventNames]);

  useEffect(() => {
    if (urlEventId && !isLoading && data) {
      const urlEventParsed = Number(urlEventId);
      const isAvailable = data?.events.some((x) =>
        x.events.some((e) => e.id === urlEventParsed),
      );

      if (isAvailable) {
        setTimeout(() => {
          setShowEventId(urlEventParsed);
        }, 200);
      }
    }
  }, [urlEventId, isLoading, data]);

  // Keeps the URL's eventId in sync with the open dialog. Importantly, it must
  // NOT strip a deep-linked eventId on initial load (before the dialog has had a
  // chance to open) — otherwise a direct link to a hidden class loses its id
  // before GetEvent can resolve it. So we only remove eventId when the dialog
  // actually transitions from open → closed.
  const prevShowEventIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const hadDialogOpen = prevShowEventIdRef.current !== undefined;
    prevShowEventIdRef.current = showEventId;

    try {
      if (showEventId) {
        searchParams.set("eventId", showEventId.toString());
        setSearchParams(searchParams, { replace: true });
      } else if (hadDialogOpen) {
        // The user closed the dialog → drop eventId from the URL.
        searchParams.delete("eventId");
        setSearchParams(searchParams, { replace: true });
      }
      // else: initial load with no dialog open yet — leave eventId untouched so
      // the deep-link (including hidden classes) can resolve and open.
    } catch {
      // SecurityError can occur in private browsing mode on mobile Firefox
      // when history.replaceState is blocked - silently ignore
    }
  }, [showEventId, isLoading]);

  const urlDate = searchParams.get("date");

  const parseDate = (dateString: string) => {
    if (!dateString) return new Date();
    const [year, month, day] = dateString.split("-").map(Number);

    return new Date(year, month - 1, day);
  };

  const [selectedValue, setSelectedValue] = useState<Date>(() => {
    return urlDate ? parseDate(urlDate) : new Date();
  });

  // Navigate to event's date when eventId is provided
  // This ensures the date always matches the event, regardless of URL date parameter
  useEffect(() => {
    if (
      urlEventId &&
      !isLoading &&
      data &&
      data.events &&
      data.events.length > 0
    ) {
      const urlEventParsed = Number(urlEventId);

      if (!isNaN(urlEventParsed)) {
        // Search through ALL date groups to find the event by ID only
        const eventDateGroup = data.events.find(
          (dateGroup) =>
            dateGroup.events &&
            dateGroup.events.some((e) => e.id === urlEventParsed),
        );

        if (eventDateGroup && eventDateGroup.date) {
          // Small delay to ensure data is fully processed
          setTimeout(() => {
            const eventDate = new Date(eventDateGroup.date);
            setSelectedValue(eventDate);
          }, 100);
        }
      }
    }
  }, [urlEventId, isLoading, data]);

  // Handle late-loading date parameter (e.g., from Instagram in-app browser)
  // Only applies when there's NO eventId (otherwise eventId takes precedence)
  useEffect(() => {
    if (urlDate && !urlEventId) {
      const parsedDate = parseDate(urlDate);
      // Only update if the date is different to avoid infinite loops
      if (parsedDate.toDateString() !== selectedValue.toDateString()) {
        setSelectedValue(parsedDate);
      }
    }
  }, [urlDate, urlEventId]);

  const handleDateChange = (value: Value) => {
    if (value instanceof Date) {
      setSelectedValue(value);
    } else if (Array.isArray(value)) {
      // Handle date range selection (if applicable)
      // For example, you might want to set the start date of the range
      setSelectedValue(value[0] as Date);
    } else if (value === null) {
      // Handle null case (if applicable)
      // For example, you might want to reset the selected date
      setSelectedValue(new Date());
    }
  };

  useEffect(() => {
    if (selectedValue) {
      try {
        const year = selectedValue.getFullYear();
        const month = String(selectedValue.getMonth() + 1).padStart(2, "0");
        const day = String(selectedValue.getDate()).padStart(2, "0");
        const formattedDate = `${year}-${month}-${day}`;

        searchParams.set("date", formattedDate);
        setSearchParams(searchParams, { replace: true });
      } catch {
        // SecurityError can occur in private browsing mode on mobile Firefox
        // when history.replaceState is blocked - silently ignore
      }
    }
  }, [selectedValue, setSearchParams, searchParams]);

  const eventsForCurrentDate = useMemo(() => {
    const dayEvents = data?.events.find(
      (x) =>
        dateUtility.formatDate(x.date) ===
        dateUtility.formatDate(selectedValue as Date),
    )?.events;

    if (!dayEvents || !selectedEventName) return dayEvents;

    return dayEvents.filter(
      (e) => e.name.trim().toLowerCase() === selectedEventName.toLowerCase(),
    );
  }, [selectedValue, isLoading, data, selectedEventName]);

  const currentlySelectedEvent = useMemo(
    () =>
      eventsForCurrentDate?.find((x) => x.id === showEventId) ??
      (hiddenEvent?.id === showEventId ? hiddenEvent : undefined),
    [showEventId, eventsForCurrentDate, hiddenEvent],
  );

  const usersWithoutReservation = useMemo(() => {
    if (!data?.responsibleForUsers || !currentlySelectedEvent) return [];

    const reservedUserIds = currentlySelectedEvent.userReservations
      .filter((r) => isReservationActive(r.isCancelled, r.status))
      .map((r) => r.userId);

    return data?.responsibleForUsers.filter(
      (u) => !reservedUserIds.includes(u.id),
    );
  }, [data?.responsibleForUsers, currentlySelectedEvent]);

  const tileContent = ({ date }: TileContentProps): React.ReactNode => {
    const formattedDate = dateUtility.formatDateForHref(new Date(date));

    let hasAvailableSpaces = false;
    let hasEventsOnThatDay = false;

    data?.events.forEach((dayGroup) => {
      const eventDate = dateUtility.formatDateForHref(new Date(dayGroup.date));

      if (formattedDate === eventDate) {
        if (dayGroup.events && dayGroup.events.length > 0) {
          const relevantEvents = selectedEventName
            ? dayGroup.events.filter(
                (e) =>
                  e.name.trim().toLowerCase() ===
                  selectedEventName.toLowerCase(),
              )
            : dayGroup.events;

          if (relevantEvents.length > 0) {
            hasEventsOnThatDay = true;
          }

          relevantEvents.forEach((x) => {
            if (x.hasAvailableSpaces && !x.isService) {
              hasAvailableSpaces = true;
            }
          });
        }
      }
    });

    // Return null if no events at all
    if (!hasEventsOnThatDay) {
      return null;
    }

    // Green if at least one event has available spaces
    if (hasAvailableSpaces) {
      return (
        <span
          className={classNames(
            styles.calendar_event_indicator,
            styles.calendar_event_indicator_green,
          )}
        />
      );
    }

    return (
      <span
        className={classNames(
          styles.calendar_event_indicator,
          styles.calendar_event_indicator_red,
        )}
      />
    );
  };

  const onTryEventSignUp = (eventId?: number) => {
    if (data?.responsibleForUsers && data?.responsibleForUsers?.length > 1) {
      setShowUserSelectionDialog(true);
    } else {
      const firstUser = data?.responsibleForUsers[0];
      onEventSignUp(eventId, firstUser?.id);
    }
  };

  const onEventSignUp = (eventId?: number, userId?: number) => {
    setShowEventId(undefined);
    setShowUserSelectionDialog(false);
    if (!eventId || !userId) {
      return;
    }

    const evt =
      eventsForCurrentDate?.find((x) => x.id === eventId) ??
      (hiddenEvent?.id === eventId ? hiddenEvent : undefined);

    // Pre-reservation consent form: block until the user fills it in and agrees.
    // The submission is remembered per event/target, and re-required if either changes.
    const hasSubmissionForTarget =
      formSubmissionRef.current?.eventId === eventId &&
      formSubmissionRef.current?.userId === userId;
    if (evt?.preReservationFormId && !hasSubmissionForTarget) {
      formSubmissionRef.current = null;
      setFormDialogContext({ eventId, userId });
      return;
    }

    // For prepaid events, and events that accept Multisport, gate the reservation on the
    // payment-method dialog so the user can choose between card / subscription plan /
    // Multisport. Events without available spaces skip straight to the waiting list —
    // there is no confirmed spot to pay for yet.
    if (
      (evt?.requirePrepayment || evt?.isMultisportAvailable) &&
      evt?.hasAvailableSpaces
    ) {
      setPrepaymentDialogContext({ eventId, userId });
      return;
    }

    performReserve(eventId, userId, null, false);
  };

  const onFormDialogAgree = (answers: string[]) => {
    if (!formDialogContext || !preReservationForm?.id) {
      return;
    }
    const { eventId, userId } = formDialogContext;
    formSubmissionRef.current = {
      eventId,
      userId,
      submission: {
        preReservationFormId: preReservationForm.id,
        answers,
      },
    };
    setFormDialogContext(null);
    // Re-run: the form gate now passes and the flow proceeds to prepayment/reserve.
    onEventSignUp(eventId, userId);
  };

  const onFormDialogDisagree = () => {
    setFormDialogContext(null);
    getErrorToast(
      "Резервацията не може да бъде направена без да попълните формуляра.",
    );
  };

  const performReserve = (
    eventId: number,
    userId: number,
    subscriptionPlanId: number | null,
    payWithMultisport: boolean = false,
  ) => {
    setPrepaymentDialogContext(null);
    const formSubmission =
      formSubmissionRef.current?.eventId === eventId &&
      formSubmissionRef.current?.userId === userId
        ? formSubmissionRef.current.submission
        : null;
    mutate(
      {
        endpoint: endpointsMetadata.portalCalendar.reserve,
        globalIsLoading: true,
        method: "post",
        disableSuccessToast: true,
        ...{ eventId, userId, subscriptionPlanId, payWithMultisport, formSubmission },
      },
      {
        onSuccess: (data: any) => {
          // The consent snapshot was persisted with this reservation — never
          // reuse it for a subsequent reservation (e.g. for a family member).
          formSubmissionRef.current = null;
          if (data?.stripeCheckoutUrl) {
            // Don't invalidate here — the refetch would be aborted during navigation
            // and trigger a spurious error toast (especially on iOS)
            window.location.href = data.stripeCheckoutUrl;
          } else if (data?.reservationId || Number(data)) {
            queryClient.invalidateQueries({
              queryKey: [endpointsMetadata.portalCalendar.getAll],
            });
            const reservationId = data?.reservationId ?? Number(data);
            navigate(
              `${routePaths.portalReservationSuccess}?reservationId=${reservationId}`,
            );
          } else {
            getErrorToast(
              "Възникна грешка със Stripe интеграцията. Свържете се с администратор на обекта.",
            );
          }
        },
      },
    );
  };

  const onPrepaymentDialogConfirm = (result: IPrepaymentDialogConfirmResult) => {
    if (!prepaymentDialogContext) return;
    const { eventId, userId } = prepaymentDialogContext;
    performReserve(
      eventId,
      userId,
      result.subscriptionPlanId,
      result.payWithMultisport,
    );
  };

  const onPrepaymentDialogCancel = () => {
    setPrepaymentDialogContext(null);
  };

  const prepaymentDialogEvent = useMemo(() => {
    if (!prepaymentDialogContext) return undefined;
    return (
      eventsForCurrentDate?.find(
        (x) => x.id === prepaymentDialogContext.eventId,
      ) ??
      (hiddenEvent?.id === prepaymentDialogContext.eventId
        ? hiddenEvent
        : undefined)
    );
  }, [prepaymentDialogContext, eventsForCurrentDate, hiddenEvent]);

  // The consent form quotes the class (name, price, time), so the dialog needs the
  // event it gates — resolved the same way as the prepayment dialog's.
  const formDialogEvent = useMemo(() => {
    if (!formDialogContext) return undefined;
    return (
      eventsForCurrentDate?.find((x) => x.id === formDialogContext.eventId) ??
      (hiddenEvent?.id === formDialogContext.eventId ? hiddenEvent : undefined)
    );
  }, [formDialogContext, eventsForCurrentDate, hiddenEvent]);

  return {
    isLoading,
    eventsForCurrentDate,
    selectedValue,
    responsibleForUsersData: data?.responsibleForUsers,
    visibleUntil: data?.visibleUntil,
    isDesktopOrTablet,
    showEventId,
    showUserSelectionDialog,
    currentlySelectedEvent,
    usersWithoutReservation,
    distinctEventNames,
    selectedEventName,
    setSelectedEventName,
    setShowUserSelectionDialog,
    setShowEventId,
    onTryEventSignUp,
    onEventSignUp,
    tileContent,
    handleDateChange,
    prepaymentDialogContext,
    prepaymentDialogEvent,
    onPrepaymentDialogConfirm,
    onPrepaymentDialogCancel,
    preReservationForm,
    formDialogEvent,
    showFormDialog: !!formDialogContext && !!preReservationForm?.id,
    onFormDialogAgree,
    onFormDialogDisagree,
  };
};
